import { useEffect, useState } from 'react';
import SideMenu from '../components/Menu';
import { useAuth } from '../context/AuthContext';
import { useAnalytics } from '../context/AnalyticsContext';
import AnalyticsTabs from '../components/AuthAnalytics/AnalyticsTabs';
import AnalyticsGraph from '../components/AuthAnalytics/AnalyticsGraph';
import TopicsPieChart from '../components/AuthAnalytics/AnalyticsPie';
import AuthorDetailsModal from '../components/modals/AuthorDetailsModal';
import LoadingModal from '../components/modals/LoadingModal';

const Analytics = () => {
  const { myAnalytics, fetchAnalytics } = useAnalytics();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingCoAuthorAnalytics, setLoadingCoAuthorAnalytics] = useState<boolean>(false);
  const [visibleAuthorsCount, setVisibleAuthorsCount] = useState(24);
  const [moreAuthors, setMoreAuthors] = useState<boolean>(true);
  const [isScrolled, setIsScrolled] = useState<boolean>(false);
  const [selectedCoAuthorData, setSelectedCoAuthorData] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState('');

  const filteredCoAuthors = myAnalytics
    ? myAnalytics.coAuthors.filter(
        coAuthor =>
          coAuthor.displayName.toLowerCase().includes(searchTerm.toLowerCase()) ||
          coAuthor.institution?.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : [];

  useEffect(() => {
    if (filteredCoAuthors.length > visibleAuthorsCount) {
      setMoreAuthors(true);
    } else {
      setMoreAuthors(false);
    }
  }, [filteredCoAuthors, visibleAuthorsCount]);

  useEffect(() => {
    const fetchIfNeeded = async () => {
      if (!myAnalytics && user?.openAlexId) {
        setLoading(true);
        await fetchAnalytics(user.openAlexId);
        setLoading(false);
      }
    };
    fetchIfNeeded();
  }, [user?.openAlexId]);

  const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
    const scrollTop = event.currentTarget.scrollTop;
    setIsScrolled(scrollTop > 3);
  };

  if (!myAnalytics || loading) {
    return (
      <>
        <SideMenu />
        <div className="p-3 h-screen bg-accent w-full flex flex-col">
          <div className="bg-white rounded-lg h-full flex items-center justify-center">
            <div className="text-center text-gray-700 text-lg">
              <div
                className="loader animate-spin rounded-full h-12 w-12 border-t-4 border-b-4"
                style={{ borderColor: '#5BC0BE' }}
              />
            </div>
          </div>
        </div>
      </>
    );
  }

  const selectCoAuthor = async (coAuthorId: string) => {
    setLoadingCoAuthorAnalytics(true);
    try {
      const response = await fetch(
        `https://academichub-backend.onrender.com/coauthoranalytics?id=${coAuthorId}`,
        {
          method: 'GET',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed toLowerCase() fetch co-author analytics');
      }
      const coAuthorData = await response.json();
      setSelectedCoAuthorData(coAuthorData);
      setIsModalOpen(true);
    } catch (error) {
      console.error('Error retrieving co-author analytics', error);
    } finally {
      setLoadingCoAuthorAnalytics(false);
    }
  };

  return (
    <>
      <SideMenu />
      <div id="myAnalytics" className="p-3 h-screen bg-accent w-full">
        <div
          className="bg-white rounded-lg h-full overflow-y-auto relative"
          onScroll={handleScroll}
        >
          <div
            className={`sticky top-0 z-10 bg-white transition-shadow duration-200 ${
              isScrolled ? 'shadow-md' : ''
            }`}
          >
            <div className="px-6 pt-4 pb-2">
              <AnalyticsTabs activeTab={activeTab} setActiveTab={setActiveTab} />
            </div>
            <div className="px-6 pb-2">
              {activeTab === 'overview' && (
                <h2 className="text-3xl font-bold mb-5 pt-5 px-6 text-center">Research Summary</h2>
              )}
              {activeTab === 'trends' && (
                <h2 className="text-3xl font-bold mb-5 pt-5 px-6 text-center">Yearly Breakdown</h2>
              )}
              {activeTab === 'collaborations' && (
                <>
                  <h2 className="text-3xl font-bold mb-5 pt-5 px-6 text-center">Collaborations</h2>
                  <div className="flex justify-center mb-4">
                    <div className="relative w-full sm:w-80">
                      <input
                        type="text"
                        placeholder="Search co-authors..."
                        className="w-full border border-gray-300 rounded-md px-3 pr-8 py-1 text-sm"
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                      />
                      <div className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400">
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M21 21L15 15M17 10C17 13.866 13.866 17 10 17C6.13401 17 3 13.866 3 10C3 6.13401 6.13401 3 10 3C13.866 3 17 6.13401 17 10Z"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </div>
                    </div>
                  </div>
                </>
              )}
              {activeTab === 'affiliations' && (
                <h2 className="text-3xl font-bold mb-5 pt-5 px-6 text-center">Affiliations</h2>
              )}
            </div>
          </div>
          {isScrolled && (
            <div
              style={{ backgroundColor: 'rgba(255,255,255,1)' }}
              className="w-full h-[3px] shadow-md transition-opacity duration-200"
            />
          )}

          {activeTab === 'overview' && (
            <div className="pt-8 pb-32 px-8 flex flex-col items-center">
              <div className="w-full max-w-6xl grid grid-cols-1 md:grid-cols-2 gap-8 mb-10">
                <div className="bg-white shadow-lg rounded-lg p-8">
                  <div className="flex items-center mb-6">
                    <svg
                      className="w-8 h-8 text-teal-500 mr-3"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M5.121 17.804A8 8 0 1118.879 6.196M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                      />
                    </svg>
                    <h3 className="text-2xl font-semibold text-[#1d244f]">Profile</h3>
                  </div>
                  <div className="text-gray-700 space-y-4 text-lg leading-relaxed">
                    <p>
                      <span className="font-medium">Name: </span>
                      {myAnalytics.authorName}
                    </p>
                    <p>
                      <span className="font-medium">OpenAlex ID: </span>
                      {myAnalytics.openAlexId.split('/').pop() || 'N/A'}
                    </p>
                    <p>
                      <span className="font-medium">Scopus ID: </span>
                      {myAnalytics.ids?.scopusId || 'N/A'}
                    </p>
                    <p>
                      <span className="font-medium">ORCiD ID: </span>
                      {myAnalytics.ids?.orcidId || 'N/A'}
                    </p>
                  </div>
                </div>
                <div className="bg-white shadow-lg rounded-lg p-8">
                  <div className="flex items-center mb-6">
                    <svg
                      className="w-8 h-8 text-teal-500 mr-3"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M3 10h4v11H3zM10 5h4v16h-4zM17 13h4v8h-4z"
                      />
                    </svg>
                    <h3 className="text-2xl font-semibold text-[#1d244f]">Indices</h3>
                  </div>
                  <div className="text-gray-700 space-y-4 text-lg leading-relaxed">
                    <p>
                      <span className="font-medium">H-Index: </span>
                      {myAnalytics.summaryStats.h_index ?? 'N/A'}
                    </p>
                    <p>
                      <span className="font-medium">i10-Index: </span>
                      {myAnalytics.summaryStats.i10_index ?? 'N/A'}
                    </p>
                    <p>
                      <span className="font-medium">m-index: </span>
                      {myAnalytics.calculatedMetrics?.mIndex
                        ? myAnalytics.calculatedMetrics.mIndex.toFixed(2)
                        : 'N/A'}
                    </p>
                  </div>
                </div>
              </div>
              <div className="w-full max-w-6xl bg-white shadow-lg rounded-lg p-8 mb-8">
                <div className="flex items-center mb-6">
                  <svg
                    className="w-8 h-8 text-teal-500 mr-3"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 17l6-6 4 4 8-8" />
                  </svg>
                  <h3 className="text-2xl font-semibold text-[#1d244f]">Impact</h3>
                </div>
                <div className="flex flex-col md:flex-row gap-6">
                  <div className="w-full md:w-2/3 space-y-4 text-gray-700 text-lg leading-relaxed">
                    <p>
                      <span className="font-medium">Total Works: </span>
                      {myAnalytics.totalCount}
                    </p>
                    <p>
                      <span className="font-medium">Total Citations: </span>
                      {myAnalytics.totalCitations}
                    </p>
                    <p>
                      <span className="font-medium">Average Citations/Year: </span>
                      {myAnalytics.calculatedMetrics.citationsPerYear?.toFixed(0) || 'N/A'}
                    </p>
                  </div>
                  <div className="w-full md:w-1/3 flex flex-col gap-4">
                    <div className="bg-[#0B132B] text-white shadow-md rounded-lg p-4 text-center">
                      <h4 className="font-semibold text-base mb-2">Peak Publication Year</h4>
                      <p className="text-sm leading-relaxed">
                        Year:{' '}
                        <span className="font-medium">
                          {myAnalytics.calculatedMetrics.peakPublicationYear ?? 'N/A'}
                        </span>
                      </p>
                      <p className="text-sm leading-relaxed">
                        Publications:{' '}
                        <span className="font-medium">
                          {myAnalytics.calculatedMetrics.peakWorksCount ?? 'N/A'}
                        </span>
                      </p>
                    </div>
                    <div className="bg-[#0B132B] text-white shadow-md rounded-lg p-4 text-center">
                      <h4 className="font-semibold text-base mb-2">Peak Citation Year</h4>
                      <p className="text-sm leading-relaxed">
                        Year:{' '}
                        <span className="font-medium">
                          {myAnalytics.calculatedMetrics.peakCitationYear ?? 'N/A'}
                        </span>
                      </p>
                      <p className="text-sm leading-relaxed">
                        Citations:{' '}
                        <span className="font-medium">
                          {myAnalytics.calculatedMetrics.peakCitedByCount ?? 'N/A'}
                        </span>
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="w-full max-w-6xl bg-white shadow-lg rounded-lg p-8">
                <div className="flex items-center mb-6">
                  <svg
                    className="w-8 h-8 text-teal-500 mr-3"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 20h18M3 4h18" />
                  </svg>
                  <h3 className="text-2xl font-semibold text-[#1d244f]">Research Topics</h3>
                </div>
                {myAnalytics.researchTopics ? (
                  <div className="pb-10">
                    <TopicsPieChart topics={myAnalytics.researchTopics} />
                  </div>
                ) : (
                  <p>No topics data available.</p>
                )}
              </div>
            </div>
          )}
          {activeTab === 'trends' && (
            <div className="flex flex-col items-center pt-6 pb-24">
              <div className="w-full max-w-8xl px-8 mx-auto">
                <AnalyticsGraph
                  title="Citations Per Year"
                  data={myAnalytics.countsByYear}
                  color="rgba(54, 162, 235, 0.6)"
                />
                <AnalyticsGraph
                  title="Publications Per Year"
                  data={myAnalytics.countsByYear}
                  color="rgba(11, 19, 43, 0.6)"
                />
              </div>
            </div>
          )}
          {activeTab === 'collaborations' && (
            <div className="flex flex-col items-center pt-6 pb-24">
              <div className="pb-24 w-full max-w-4xl">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {filteredCoAuthors.slice(0, visibleAuthorsCount).map(coAuthor => (
                    <div
                      key={coAuthor.id}
                      className="
                        border p-4 rounded-lg shadow-sm flex flex-col justify-center items-center 
                        transition-all duration-300 ease-in-out cursor-pointer 
                        hover:scale-105 hover:bg-[#5BC0BE] hover:text-white"
                      onClick={() => {
                        const coAuthorId = coAuthor.id?.split('/').pop();
                        if (coAuthorId) selectCoAuthor(coAuthorId);
                      }}
                    >
                      <h3 className="text-medium font-semibold">{coAuthor.displayName}</h3>
                      <p className="text-gray-500 text-center">
                        {coAuthor.institution ? coAuthor.institution : 'Institution not available'}
                      </p>
                    </div>
                  ))}
                </div>
                {moreAuthors && (
                  <div className="pt-5">
                    <button
                      className="mt-4 px-4 py-2 bg-gray-200 text-[#1d244f] rounded transition-all duration-300 ease-in-out hover:bg-[#0B132B] hover:text-white mx-auto block"
                      onClick={() => setVisibleAuthorsCount(visibleAuthorsCount + 24)}
                    >
                      More
                    </button>
                  </div>
                )}
              </div>
              {selectedCoAuthorData && isModalOpen && (
                <AuthorDetailsModal
                  data={selectedCoAuthorData}
                  onClose={() => {
                    setSelectedCoAuthorData(null);
                    setIsModalOpen(false);
                  }}
                />
              )}
              {loadingCoAuthorAnalytics && (
                <LoadingModal
                  onClose={() => {
                    setSelectedCoAuthorData(null);
                    setIsModalOpen(false);
                  }}
                />
              )}
            </div>
          )}
          {activeTab === 'affiliations' && (
            <div className="pb-24 px-6 flex flex-col items-center">
              <p className="text-center text-gray-600 text-base mb-8 px-4 max-w-2xl">
                Institutions this author has been affiliated with, as claimed in their published
                works.
              </p>
              <div className="relative ml-6 border-l-4 border-accent/60">
                {myAnalytics.affiliations.map((inst, index) => (
                  <div key={index} className="relative pl-6 py-6">
                    <div className="absolute left-[-10px] top-6 w-3 h-3 bg-accent rounded-full" />
                    <h3 className="text-xl font-semibold text-[#1d244f]">{inst.name}</h3>
                    <p className="text-gray-600">
                      {inst.startYear} - {inst.endYear || 'Present'}
                    </p>
                    {inst.rorId && (
                      <a
                        href={inst.rorId}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-teal-600 underline"
                      >
                        View on ROR
                      </a>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default Analytics;
